import React from 'react';
import { getActiveGoals, getGoalActual } from '../utils/goalTypes';

/**
 * Bauhaus "Albers Squares" daily goal indicator.
 * compact = 2 nested squares (Wörter + drills combined)
 * full    = all individual goal squares nested
 */
const DailyGoalRing = ({ dayStats, dailyGoals, size = 130, compact = false, streak }) => {
  const displaySize = compact ? 38 : size;

  const activeGoals = getActiveGoals(dailyGoals);
  const clamp = (val, max) => (max <= 0 ? 0 : Math.min(val / max, 1));

  // Build layers depending on mode
  let layers;
  if (compact) {
    const wordsGoal = activeGoals.find((g) => g.key === 'words');
    const drillGoals = activeGoals.filter((g) => g.key !== 'words');

    const wordsFill = wordsGoal
      ? clamp(getGoalActual(dayStats, wordsGoal.key), wordsGoal.target)
      : 0;
    let drillsFill = 0;
    if (drillGoals.length > 0) {
      drillsFill = drillGoals.reduce(
        (sum, g) => sum + clamp(getGoalActual(dayStats, g.key), g.target),
        0
      ) / drillGoals.length;
    }
    layers = [
      { fill: drillsFill, color: 'var(--warn-accent)' },
      { fill: wordsFill, color: '#0ea5e9' },
    ];
  } else {
    layers = activeGoals.map((g) => ({
      fill: clamp(getGoalActual(dayStats, g.key), g.target),
      color: g.color || 'var(--ink-900)',
    }));
  }

  // Percentage always computed from individual goals (same in both modes)
  const goalCount = Math.max(activeGoals.length, 1);
  const goalFillSum = activeGoals.reduce(
    (sum, g) => sum + clamp(getGoalActual(dayStats, g.key), g.target),
    0
  );
  const pct = activeGoals.length > 0 ? Math.round((goalFillSum / goalCount) * 100) : 100;
  const allMet = activeGoals.length > 0 && activeGoals.every(
    (g) => getGoalActual(dayStats, g.key) >= g.target
  );

  const padding = compact ? 2 : 6;
  const maxInset = displaySize / 2 - (compact ? 4 : 10);
  const layerCount = Math.max(layers.length, 1);
  const layerStep = maxInset / layerCount;

  const squares = (
    <svg width={displaySize} height={displaySize} viewBox={`0 0 ${displaySize} ${displaySize}`}>
      {layers.map((layer, i) => {
        const inset = padding + i * layerStep;
        const sqSize = displaySize - inset * 2;
        const perimeter = sqSize * 4;
        const fillLength = perimeter * layer.fill;
        return (
          <g key={i}>
            {/* Track in the goal's own colour, same weight as the progress
                stroke — at 0% a faint grey hairline made the meter read as
                an empty box. */}
            <rect
              x={inset} y={inset}
              width={sqSize} height={sqSize}
              fill="none"
              stroke={layer.color}
              strokeOpacity={0.2}
              strokeWidth={compact ? 2.5 : 3.5}
            />
            <rect
              x={inset} y={inset}
              width={sqSize} height={sqSize}
              fill={layer.fill >= 1 ? layer.color : 'none'}
              fillOpacity={layer.fill >= 1 ? 0.12 : 0}
              stroke={layer.color}
              strokeWidth={compact ? 2.5 : 3.5}
              strokeDasharray={`${fillLength} ${perimeter - fillLength}`}
              strokeDashoffset={0}
              style={{ transition: 'stroke-dasharray 0.5s ease, fill-opacity 0.5s ease' }}
            />
          </g>
        );
      })}
      <text
        x={displaySize / 2}
        y={displaySize / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fontWeight="700"
        fontFamily="Space Grotesk, sans-serif"
        fontSize={compact ? (allMet ? 13 : 9) : (allMet ? 26 : 16)}
        fill={allMet ? 'var(--success)' : 'var(--ink-900)'}
      >
        {allMet ? '\u2713' : `${pct}%`}
      </text>
    </svg>
  );

  if (compact) {
    return (
      <span style={{ position: 'relative', display: 'inline-block', width: displaySize, height: displaySize }}>
        {squares}
        {streak != null && streak > 0 && (
          <span
            style={{
              position: 'absolute', top: -4, right: -6,
              minWidth: 16, height: 16,
              background: streak >= 7 ? 'var(--ink-900)' : 'var(--ink-500)',
              color: '#fff', fontSize: 9, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '0 3px', lineHeight: 1,
            }}
            title={`${streak} Tage Streak`}
          >
            {streak}
          </span>
        )}
      </span>
    );
  }

  return squares;
};

export default DailyGoalRing;
